import { useState, useEffect } from "react";
import styles from "./CensusDrawer.module.css";
import { Formik, Form, useFormikContext } from "formik";
import {
  SystemIcon,
  Button,
  Badge,
  Avatar,
} from "@justworkshr/milo-components";
import { CheckboxInput } from "@justworkshr/milo-form";
import Alert from "uicomponents/Alert";
import TextInput from "uicomponents/TextInput";
import RadioInput from "uicomponents/RadioInput";
import TextArea from "uicomponents/TextArea";
import SelectInput from "uicomponents/SelectInput";
import { stateOption } from "pages/WorkersCompApplcation/utils/selectInputOptions";
import { baseWCSchemaShape } from "./utils/validationSchema";
import { SingleCensusType } from "types";
import { getMemberUuid } from "utils/idHelpers";
import {
  convertBoolIntoString,
  getOfficeFullAddress,
  hasQueryParam,
} from "utils/helper";
import { EmployeeOfficeList } from "types";
import { useUpdateOwnerAndEmployeeCensusMutation } from "types/generated/operations";
import { convertStringIntoBool } from "utils/helper";
import { EmployeeExemptionStatusEnum } from "types/generated/schema";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import {
  trackFormError,
  trackCoreAppFormError,
} from "utils/analytics/trackEvent";
import { Alert as AlertNotice } from "@justworkshr/milo-core";
import { getNeonRedirectUrl } from "utils/helper";

interface CensusDrawerProps {
  isOpen: boolean;
  closeDrawer: (refetch?: boolean) => void;
  initialData?: SingleCensusType;
  officeLocations?: EmployeeOfficeList;
}

const CensusDrawer = ({
  initialData,
  isOpen,
  closeDrawer,
  officeLocations,
}: CensusDrawerProps) => {
  const [hasError, setError] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState<{
    entered: BusinessAddressFormType | null;
    suggested: BusinessAddressFormType | null;
  }>({ entered: null, suggested: null });
  const [storedSubmittedValues, setStoredSubmittedValues] = useState<
    any | null
  >(null);

  const openClass = isOpen ? styles.open : "";
  const wc_flow_with_core_app = hasQueryParam("usecoreapp");
  const isFirstUser = initialData?.uuid === getMemberUuid();
  const labelList = sectionsQuestions["census"].questions;
  const messageList = sectionsQuestions["census"].message;
  const firstUserLabelList = sectionsQuestions["census"].firstUserQuestions;

  const [showCoreAppFieldsError, setShowCoreAppFieldsError] = useState(false);

  const formatForRadio = (enumValue: string | null | undefined): string => {
    if (enumValue == null) {
      return "";
    }
    return enumValue
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/^./, (c) => c.toUpperCase());
  };

  const defaults = {
    firstName: initialData?.firstName || "",
    middleName: initialData?.middleName || "",
    lastName: initialData?.lastName || "",
    title: initialData?.employeeInformation?.title || "",
    roleDescription: initialData?.employeeInformation?.roleDescription || "",
    ownerStake: initialData?.employeeInformation?.ownerStake ?? "",
    isPaid: initialData?.employeeInformation?.isPaid || "",
    salary: initialData?.employeeInformation?.salary || "",
    email: initialData?.email || "",
    employeeType: initialData?.employeeType || "",
    adminAccess: initialData?.employeeInformation?.adminAccess ?? undefined,
    isCompanyOwner: initialData?.isCompanyOwner ?? undefined,
    isCompanyEmployee: initialData?.isCompanyEmployee ?? undefined,
    salaryType: initialData?.employeeInformation?.salaryType || "",
    hourlyRate: initialData?.employeeInformation?.hourlyRate || "",
    hoursWeekly: initialData?.employeeInformation?.hoursWeekly || "",
    officeLocationId: initialData?.officeLocationId || "",
    exemptionStatus:
      formatForRadio(initialData?.employeeInformation?.exemptionStatus) ??
      undefined,
    isRemote:
      convertBoolIntoString(initialData?.employeeInformation?.isRemote) ?? "",
    address1: initialData?.employeeInformation?.remoteAddress?.address1 || "",
    address2: initialData?.employeeInformation?.remoteAddress?.address2 || "",
    state: initialData?.employeeInformation?.remoteAddress?.state || "",
    city: initialData?.employeeInformation?.remoteAddress?.city || "",
    zip: initialData?.employeeInformation?.remoteAddress?.zip || "",
    uuid: initialData?.uuid || "",
  };

  const [updateCensus, { loading: updateCensusLoading }] =
    useUpdateOwnerAndEmployeeCensusMutation();

  const handleSubmission = async (values: any) => {
    setError(false);
    try {
      const formattedEmployee = {
        uuid: values.uuid,
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        title: values.title,
        roleDescription: values.roleDescription,
        ownerStake: values.isCompanyOwner
          ? Number(values.ownerStake)
          : undefined,
        isPaid: values.isPaid,
        salary: Number(values.salary),
        email: values.email,
        employeeType: values.isCompanyEmployee
          ? values.employeeType
          : undefined,
        adminAccess: values.isCompanyEmployee
          ? values.adminAccess || false
          : undefined,
        isCompanyOwner: values.isCompanyOwner,
        isCompanyEmployee: values.isCompanyEmployee,
        salaryType: values.salaryType,
        hourlyRate: Number(values.hourlyRate),
        hoursWeekly: Number(values.hoursWeekly),
        officeLocationId: values.officeLocationId || undefined,
        exemptionStatus:
          (values.exemptionStatus
            ?.toUpperCase()
            .replace(/-/g, "_") as EmployeeExemptionStatusEnum) || null,
        isRemote: convertStringIntoBool(values.isRemote) || false,
        remoteAddress: convertStringIntoBool(values.isRemote)
          ? {
              address1: values.address1 || "",
              address2: values.address2 || "",
              state: values.state || "",
              city: values.city || "",
              zip: values.zip || "",
            }
          : undefined,
      };

      const updateCensusResponse = await updateCensus({
        variables: {
          ...formattedEmployee,
        },
      });

      const responseData = updateCensusResponse.data?.updateEmployeeCensus;
      const autoValidated = responseData?.addressAutoValidated;
      const suggestedAddressResponse = responseData?.suggestedAddress;

      if (
        convertStringIntoBool(values.isRemote) &&
        autoValidated === false &&
        suggestedAddressResponse?.address1
      ) {
        setStoredSubmittedValues(values);
        setSuggestedAddresses({
          entered: {
            uuid: "",
            locationName: "",
            address1: values.address1 || "",
            address2: values.address2 || "",
            city: values.city || "",
            state: values.state || "",
            zip: values.zip || "",
          },
          suggested: {
            uuid: "",
            locationName: "",
            address1: suggestedAddressResponse.address1 || "",
            address2: suggestedAddressResponse.address2 || "",
            city: suggestedAddressResponse.city || "",
            state: suggestedAddressResponse.state || "",
            zip: suggestedAddressResponse.zip || "",
          },
        });
        return;
      }

      closeDrawer(true);
      setSuggestedAddresses({ entered: null, suggested: null });
      setStoredSubmittedValues(null);
    } catch (error: any) {
      trackFormError(error, "Error submitting workers comp census");
      setError(true);
    }
  };

  const handleKeepCurrent = () => {
    if (storedSubmittedValues) {
      setSuggestedAddresses({ entered: null, suggested: null });
      setStoredSubmittedValues(null);
      closeDrawer(true);
    }
  };

  const handleUseSuggestion = async (
    suggestedAddress: BusinessAddressFormType
  ) => {
    if (storedSubmittedValues) {
      const updatedValues = {
        ...storedSubmittedValues,
        address1: suggestedAddress.address1,
        address2: suggestedAddress.address2,
        city: suggestedAddress.city,
        state: suggestedAddress.state,
        zip: suggestedAddress.zip,
      };
      setSuggestedAddresses({ entered: null, suggested: null });
      await handleSubmission(updatedValues);
    }
  };

  const officeLocationOptions = officeLocations?.map((address: any) => ({
    value: address.uuid,
    description: address ? getOfficeFullAddress(address) : "",
  }));

  const validationSchema: any = baseWCSchemaShape({
    isOwner: initialData?.isCompanyOwner ?? undefined,
    isCompanyEmployee: initialData?.isCompanyEmployee ?? undefined,
    isPaid: initialData?.employeeInformation?.isPaid === "Paid",
    isFirstUser: isFirstUser,
  });

  const scrollToError = (fieldName: string) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const FormikValueWatcher = () => {
    const { values, setFieldValue } = useFormikContext<any>();

    useEffect(() => {
      if (values.isRemote === "No") {
        setFieldValue("address1", "");
        setFieldValue("address2", "");
        setFieldValue("state", "");
        setFieldValue("city", "");
        setFieldValue("zip", "");
      } else if (values.isRemote === "Yes") {
        setFieldValue("officeLocationId", "");
      }
    }, [values.isRemote, setFieldValue]);

    useEffect(() => {
      if (values.salaryType === "Annual salary") {
        setFieldValue("hourlyRate", "");
        setFieldValue("hoursWeekly", "");
      } else if (values.salaryType === "Hourly rate") {
        setFieldValue("salary", "");
      }
    }, [values.salaryType, setFieldValue]);

    return null;
  };

  return (
    <>
      {suggestedAddresses.entered && suggestedAddresses.suggested && (
        <SuggestedAddressModel
          enteredAddress={suggestedAddresses.entered}
          suggestedAddress={suggestedAddresses.suggested}
          onUseSuggestion={handleUseSuggestion}
          onKeepCurrent={handleKeepCurrent}
        />
      )}
      <div className={`${styles.drawerWrapper} ${openClass}`}>
        <div className={styles.drawerContent}>
          <div className={styles.drawerHeader}>
            <h1>Update {initialData?.firstName}'s info</h1>
            <button
              className={styles.closeButton}
              onClick={() => closeDrawer()}
              aria-label="Close drawer"
              aria-roledescription="button"
            >
              <SystemIcon iconName="close" />
            </button>
          </div>
          <Formik
            initialValues={defaults}
            onSubmit={handleSubmission}
            validationSchema={validationSchema}
            enableReinitialize={true}
          >
            {({ errors, handleSubmit, values, setFieldValue }) => {
              return (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const currentValidationErrors = Object.keys(errors);
                    const firstErrorFieldName = currentValidationErrors[0];

                    // Handle missing fields from potential failed core app submission
                    if (wc_flow_with_core_app) {
                      const coreAppRequiredFields = [
                        "isPaid",
                        "ownerStake",
                        "email",
                        "employeeType",
                      ];
                      const missingCoreAppFields = coreAppRequiredFields.filter(
                        (field) => currentValidationErrors.includes(field)
                      );
                      const hasMissingCoreAppFields =
                        missingCoreAppFields.length > 0;
                      setShowCoreAppFieldsError(hasMissingCoreAppFields);
                      if (hasMissingCoreAppFields) {
                        trackCoreAppFormError(missingCoreAppFields);
                      }
                    }

                    scrollToError(firstErrorFieldName);
                    handleSubmit(e);
                  }}
                >
                  <FormikValueWatcher />
                  <div className={styles.drawerBody}>
                    <div className={styles.drawerEmployeeInfo}>
                      <Avatar
                        name={`${initialData?.firstName} ${initialData?.lastName}`}
                        size="md"
                      />
                      <div className={styles.employeeInfoRight}>
                        <h4>
                          {initialData?.firstName} {initialData?.lastName}
                          {isFirstUser && (
                            <Badge
                              className={styles.firstUserBadge}
                              emphasis="subtle"
                              role="neutral"
                              children={"You"}
                            />
                          )}
                        </h4>
                        <p>
                          {initialData?.isCompanyOwner ? "Owner" : "Employee"}
                          {initialData?.isCompanyOwner &&
                            (initialData?.employeeInformation?.isPaid === "Paid"
                              ? " | Paid"
                              : " | Unpaid")}
                        </p>
                      </div>
                    </div>

                    {!wc_flow_with_core_app ? (
                      values.isCompanyOwner ? (
                        <TextInput
                          label={labelList.ownerStake}
                          name="ownerStake"
                          className={styles.drawerInput}
                        />
                      ) : (
                        <div
                          className={`${styles.drawerInput} ${styles.adminInput}`}
                        >
                          <CheckboxInput
                            checked={values.adminAccess || false}
                            name="adminAccess"
                            value="Yes"
                            label="Admin access"
                            onChange={(e) => {
                              setFieldValue("adminAccess", e.target.checked);
                            }}
                          />
                          <p>
                            Admins will be granted company-level permissions
                            such as scheduling payments, managing time off
                            policies, and uploading documents.
                          </p>
                        </div>
                      )
                    ) : (
                      <></>
                    )}

                    <TextInput
                      label="Job title"
                      className={styles.drawerInput}
                      name="title"
                    />

                    <TextArea
                      label={labelList.roleDescription}
                      name="roleDescription"
                    />

                    {!wc_flow_with_core_app &&
                      !(isFirstUser && values.email) && (
                        <TextInput
                          name="email"
                          type="string"
                          label={labelList.email}
                        />
                      )}

                    <RadioInput
                      name="isRemote"
                      label={
                        isFirstUser
                          ? firstUserLabelList.isRemote
                          : labelList.isRemote
                      }
                      options={["Yes", "No"]}
                    />
                    {values.isRemote === "Yes" && (
                      <>
                        <div className={styles.remoteFields}>
                          <h4>Remote work address</h4>
                          <TextInput
                            name="address1"
                            type="string"
                            label={labelList.address1}
                            message={messageList.address1}
                          />
                          <TextInput
                            name="address2"
                            type="string"
                            label={labelList.address2}
                            required={false}
                          />
                          <TextInput
                            name="city"
                            type="string"
                            label={labelList.city}
                          />
                          <SelectInput
                            placeholder="Select an option"
                            name="state"
                            label={labelList.state}
                            options={stateOption}
                          />
                          <TextInput
                            name="zip"
                            type="string"
                            label={labelList.zip}
                          />
                        </div>
                      </>
                    )}
                    {values.isRemote === "No" && (
                      <SelectInput
                        name="officeLocationId"
                        options={officeLocationOptions}
                        label="Office locations"
                        placeholder="Select an option"
                      />
                    )}

                    {!wc_flow_with_core_app && values.isCompanyEmployee && (
                      <SelectInput
                        name="employeeType"
                        options={[
                          {
                            value: "Employee - Full Time (W-2)",
                            description: "Employee - Full Time (W-2)",
                          },
                          {
                            value: "Employee - Part Time (W-2)",
                            description: "Employee - Part Time (W-2)",
                          },
                        ]}
                        label={labelList.employeeType}
                        placeholder="Select an option"
                      />
                    )}

                    {values.isPaid === "Paid" && (
                      <>
                        <RadioInput
                          name="salaryType"
                          options={["Annual salary", "Hourly rate"]}
                          label={
                            isFirstUser
                              ? firstUserLabelList.salaryType
                              : labelList.salaryType
                          }
                        />

                        {values.salaryType === "Annual salary" && (
                          <TextInput
                            name="salary"
                            type="string"
                            label={"Annual salary"}
                          />
                        )}
                        {values.salaryType === "Hourly rate" && (
                          <>
                            <TextInput
                              name="hourlyRate"
                              type="string"
                              label={"Hourly wage"}
                            />
                            <TextInput
                              name="hoursWeekly"
                              type="string"
                              label={"Hours per week"}
                            />
                          </>
                        )}
                        <RadioInput
                          name="exemptionStatus"
                          label={labelList.exemptionStatus}
                          options={["Exempt", "Non-exempt"]}
                        />
                      </>
                    )}

                    {hasError && (
                      <Alert message="Sorry, there was an error. Please try refreshing the page." />
                    )}

                    {showCoreAppFieldsError && (
                      <AlertNotice
                        color="destructive"
                        className={styles.prefilledAlert}
                      >
                        <div className={styles.alertContent}>
                          <p>
                            Some required details from the previous step are
                            missing. Please{" "}
                            <a
                              href={`${getNeonRedirectUrl(
                                "/enrollment-hub/applications/core-app/members-form"
                              )}`}
                            >
                              {" "}
                              complete your information{" "}
                            </a>{" "}
                            in order to continue.
                          </p>
                        </div>
                      </AlertNotice>
                    )}

                    <Button
                      className={styles.saveEmployeeButton}
                      color="brand"
                      type="submit"
                      loading={updateCensusLoading}
                    >
                      Save
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CensusDrawer;
